import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useNotifier } from "@repo/shared/common";

import type { Offer } from "../types/CommercialDocumentTypes";
import { ModifyInvoiceItemInput, useUpdateDocumentDraftMutation } from "@repo/server-api";

type UseUpdateOfferOptions = {
	onSaved: (offer: Offer) => void;
};

type UseUpdateOfferResult = {
	updateOffer: (offer: Offer) => void;
	loading: boolean;
};

export function useUpdateOfferHook(options: UseUpdateOfferOptions): UseUpdateOfferResult {
	const { t } = useTranslation();
	const { notifySuccess, notifyException } = useNotifier();

	const { onSaved } = options;
	const [loading, setLoading] = useState(false);

	const [updateOfferDraftMutation] = useUpdateDocumentDraftMutation();

	const updateOffer = async (offer: Offer) => {
		setLoading(true);
		try {
			await updateOfferDraftMutation({
				variables: {
					documentId: offer.id!,
					clientContactPersonId: offer.contactPerson?.id || null,
					clientId: offer.client?.id || null,
					concludingText: offer.footerNote,
					contractorId: offer.contractor?.id || null,
					introductoryText: offer.headerNote || "",
					items: offer.items.map((item, index) => ({
						description: item.description,
						discountPercent: item.discount ?? 0,
						name: item.name,
						quantity: item.quantity,
						tax: item.tax,
						taxCode: item.taxCode,
						unit: item.unit,
						unitPriceNet: item.unitPrice,
						positionNo: index,
					})) as ModifyInvoiceItemInput[],
					notes: null,
					paymentDeadlineInDays: offer.paymentDeadlineDays ?? null,
					projectId: offer.project?.id || null,
					validUntil: null,
				},
			});

			notifySuccess(t("offer-updated"));
			onSaved(offer);
		} catch (error) {
			notifyException(error);
		} finally {
			setLoading(false);
		}
	};

	return {
		updateOffer,
		loading,
	};
}
